import lang from '@/lang'
import theme from '@/theme'
import ListColumnModel, { type ListColumnModelData } from '@/modules/base/model/list-column-model'
import type { CustomerIssue } from '@/redux/ducks/customerIssues'
import { CUSTOMER_ISSUE_STATUSES, statusLabel, type CustomerIssueStatus } from './status'

/**
 * Konfiguration der list-v2-Liste „Meine Meldungen" (FLAG:docuAiEnhancements).
 *
 * Seit 2026-09-21 laeuft die Kundenseite ueber das Listen-Framework des Hauses (ListWrapper,
 * ListHeader, Search, ListQuickFilter, List) — wie Lieferscheine, Rechnungen und Ausgaben.
 * Spalten und Schnellfilter seedet der lists-Duck beim Start als initialState
 * (redux/ducks/lists/index.js, LIST_TYPES.CUSTOMER_ISSUES_LIST_TYPE); ein Server-Setting gibt es
 * fuer diese Liste nicht. Der DMS-Endpunkt kennt nur page/limit, also sortiert, sucht, filtert
 * und blaettert ag-grid clientseitig ueber alle geladenen Meldungen.
 *
 * Reine Funktionen ohne Store-Zugriff, damit sie ohne Redux testbar bleiben.
 */

const customerIssuesLang = lang.customerIssues

export interface CustomerIssueRow extends CustomerIssue {
  /** Betreff der Zeile: der Titel, ohne Titel die Beschreibung. */
  subject: string
  /** Die letzte Nachricht an den Kunden, siehe lastMessageOf(). */
  lastMessage: string
}

/**
 * Die letzte Nachricht, die der Kunde zu dieser Meldung bekommen hat.
 *
 * `messages[]` ist die Wahrheit; `customerMessage` ist nur die Bequemlichkeit „die
 * neueste" und traegt Zeilen, die aelter als das Array sind. Ein Entwurf, der auf
 * Freigabe wartet, steht in KEINEM von beiden — der Kunde sieht ihn nicht, und genau das
 * ist der Zweck des Freigabe-Plans.
 */
export const lastMessageOf = (issue: CustomerIssue): string => {
  const thread = issue.messages ?? []
  if (thread.length > 0) { return thread[thread.length - 1].body }
  return issue.customerMessage || customerIssuesLang.noMessageYet
}

// ag-grid liest flache Felder; Betreff und letzte Nachricht werden deshalb hier abgeleitet,
// statt in der Spalte einen Renderer zu erfinden.
export const toListRow = (issue: CustomerIssue): CustomerIssueRow => ({
  ...issue,
  subject: issue.title || issue.description,
  lastMessage: lastMessageOf(issue),
})

/**
 * Schnellfilter-Namen — sie landen als `?filter=` in der URL (list-quick-filter.js liest sie beim
 * Mount), deshalb englische ASCII-Slugs statt der deutschen Wire-Werte mit Leerzeichen und Umlauten.
 */
export const CUSTOMER_ISSUE_FILTER_NAMES: Record<CustomerIssueStatus, string> = {
  Eingegangen: 'received',
  'In Bearbeitung': 'in-progress',
  Rückfrage: 'question',
  Gelöst: 'resolved',
  Geschlossen: 'closed',
}

const STATUS_STATE_COLORS: Record<CustomerIssueStatus, string> = {
  Eingegangen: theme.greyStateColor,
  'In Bearbeitung': theme.blue,
  Rückfrage: theme.orange,
  Gelöst: theme.green,
  Geschlossen: theme.greyStateColor,
}

/** Punktfarbe des generischen Status-Zellrenderers; unbekannte Wire-Werte bleiben neutral grau. */
export const statusStateColor = (status: string): string =>
  STATUS_STATE_COLORS[status as CustomerIssueStatus] ?? theme.greyStateColor

export interface CustomerIssuesQuickFilter {
  name: string
  title: string
  quickFilter: { customerStatus: { type: 'set', values: string[] } } | null
}

// „Alle" zuerst, dann ein Filter je Kundenstatus. Der Set-Filter greift auf die Spalte
// customerStatus (filter 'agSetColumnFilter'); `values` sind die Wire-Werte, gegen die
// countQuickFilter (redux/ducks/_helpers) die Zeilen fuer die Zaehler im Filter vergleicht.
export const filters: CustomerIssuesQuickFilter[] = [
  { name: 'all', title: lang.label.allCapital, quickFilter: null },
  ...CUSTOMER_ISSUE_STATUSES.map((status): CustomerIssuesQuickFilter => ({
    name: CUSTOMER_ISSUE_FILTER_NAMES[status],
    title: statusLabel(status),
    quickFilter: { customerStatus: { type: 'set', values: [status] } },
  })),
]

export class CustomerIssuesListColumnModel extends ListColumnModel {
  getStatusColor?: (value: string) => string
  // Kein Resize: list-v2 speichert Spaltenbreiten per PUT auf `setting/userlist/<name>/columns`,
  // und dieses Server-Setting existiert fuer die Meldungsliste nicht.
  resizable = false
  suppressMovable = true

  constructor(data: ListColumnModelData) {
    super(data)
    if (this.name === 'customerStatus') {
      // Farbpunkt + Text (list-v2 registriert 'genericStateCellRenderer'). Der Set-Filter ist die
      // Voraussetzung dafuer, dass der Schnellfilter `{ type: 'set' }` clientseitig greift.
      this.cellRenderer = 'genericStateCellRenderer'
      this.filter = 'agSetColumnFilter'
      this.valueFormatter = ({ value }: { value: string }) => statusLabel(value)
      this.getStatusColor = statusStateColor
      this.setFilterParam({
        values: [...CUSTOMER_ISSUE_STATUSES],
        valueFormatter: ({ value }: { value: string }) => statusLabel(value),
      })
    }
  }
}

/** Frische Spalten je Aufruf — list-v2 mutiert die Spaltenliste aus dem State (menu-Spalte, minWidth). */
export const getColumns = (): CustomerIssuesListColumnModel[] => {
  const createdAt = new CustomerIssuesListColumnModel({
    name: 'createdAt', label: customerIssuesLang.columnCreatedAt, type: 'date', active: true, width: 140,
  })
  // Neueste zuerst — dieselbe Reihenfolge, in der der DMS-Endpunkt liefert (createdAt DESC).
  Object.assign(createdAt, { sort: 'desc' })

  return [
    new CustomerIssuesListColumnModel({ name: 'customerStatus', label: customerIssuesLang.columnStatus, active: true, width: 160 }),
    new CustomerIssuesListColumnModel({ name: 'subject', label: customerIssuesLang.columnSubject, active: true, width: 260 }),
    new CustomerIssuesListColumnModel({ name: 'lastMessage', label: customerIssuesLang.columnLastMessage, active: true, width: 340 }),
    createdAt,
  ]
}

export default { filters, getColumns }
